import {
  AccessibilityType,
  AvailabilityLanguage,
  AvailabilitySeason,
  Certificate,
  Month,
  MyProductFormValues,
  OpeningHours,
  OpeningHoursDates,
  OpeningHoursException,
  OpeningHoursExceptionFormValues,
  OpeningHoursWeek,
  PricingType,
  PricingUnit,
  ProductDetails,
} from "../types";
import { HasuradbProduct, VerifiedLink } from "../graphqlQueries/getProduct";
import { formatCurrency, formatTime } from "./format";

import { MONTHS_FULL_YEAR } from "./constants";
import { NestedValue } from "react-hook-form";
import moment from "moment";
import { parseLocation } from "./location";

export const defaultPricingUnit = { label: "select.placeholder", value: "select" };

const getLinkFormData = (link?: VerifiedLink | null) => ({
  url: link?.url ?? "",
  userVerifiedAt: link?.userVerifiedAt ?? "",
  userVerifiedBy: link?.userVerifiedBy ?? "",
});

export const mapOpeningHoursToWeek = (openingHours: OpeningHours[] = []): OpeningHoursWeek => {
  return openingHours.reduce(
    (acc, hour) =>
      Object.assign(acc, {
        [hour.weekday]: {
          closed: !hour.open,
          closes: formatTime(hour.closes),
          date: hour.date || undefined,
          opens: formatTime(hour.opens),
          weekday: hour.weekday,
        },
      }),
    {}
  );
};

export const mapOpeningHoursExceptionsToFormValues = (
  exceptions: OpeningHoursException[] = []
): OpeningHoursExceptionFormValues[] => {
  return exceptions.map((exception) => ({
    openingHours: mapOpeningHoursToWeek(exception.openingHours),
    period: {
      endDate: exception.end ? moment(exception.end) : null,
      startDate: exception.start ? moment(exception.start) : null,
    } as NestedValue<OpeningHoursDates>,
  }));
};

export const mapDbProductToFormValues = (
  product: HasuradbProduct,
  isProductGroup?: boolean
): MyProductFormValues => {
  const productType = product.type;

  const productCategories = product.productTags
    ? product.productTags.map((tag) => ({
        label: "",
        value: tag.tag,
      }))
    : [];

  const targetGroups = product.productTargetGroups
    ? product.productTargetGroups.map((group) => group.targetGroupId)
    : [];

  const pricing = product.productPricings?.[0];
  const priceFrom = formatCurrency(pricing?.fromPrice, true);
  const priceTo = formatCurrency(pricing?.toPrice, true);

  const capacity = product.productCapacities?.[0];
  const capacityMin = capacity?.min ? capacity.min.toString() : null;
  const capacityMax = capacity?.max ? capacity.max.toString() : null;

  const duration = product.productDuration?.[0];
  const minutes = duration?.minutes ?? 0;
  const hours = duration?.hours ?? 0;
  const days = duration?.days ?? 0;
  const weeks = duration?.weeks ?? 0;

  const availableMonths: Month[] = product.productAvailableMonths
    ? product.productAvailableMonths.map((month) => month.month)
    : [];

  const eventAvailability = product.productAvailabilities?.[0];
  const startDate = eventAvailability?.startDate ? moment(eventAvailability.startDate) : null;
  const endDate = eventAvailability?.endDate ? moment(eventAvailability.endDate) : null;
  const transportationAvailability = eventAvailability?.transportationAvailability;

  let seasonAvailabilityType = AvailabilitySeason.NotSet;
  if (availableMonths.length > 0) {
    seasonAvailabilityType =
      availableMonths.length === MONTHS_FULL_YEAR
        ? AvailabilitySeason.AllYear
        : AvailabilitySeason.SpecificMonths;
  }

  const images = Array.isArray(
    isProductGroup
      ? product.productGroupProductImages ?? product.productImages
      : product.productImages || []
  )
    ? isProductGroup
      ? product.productGroupProductImages ?? product.productImages
      : product.productImages || []
    : [];
  const originalImages = product.productImages || [];
  const videos =
    product.productVideos.length > 0
      ? product.productVideos.map((video) => ({
          title: video.title,
          videoLink: getLinkFormData(video.verifiedVideoLink),
        }))
      : [];

  const productAddress = product.postalAddresses[0];

  const location = productAddress?.location;
  let longitude, latitude;
  if (location) {
    const parsedLocation = parseLocation(location);
    latitude = parsedLocation.latitude;
    longitude = parsedLocation.longitude;
  }

  const originalDetails = product.productInformations.map<ProductDetails>((information) => ({
    language: information.language,
    productDescription: information.description,
    productLink: getLinkFormData(information.verifiedProductLink),
    productName: information.name,
    webshopLink: getLinkFormData(information.verifiedWebshopLink),
  }));

  const details =
    isProductGroup && product.productGroupProductInformations
      ? product.productGroupProductInformations.map<ProductDetails>((information) => ({
          language: information.language,
          productDescription: information.description,
          productLink: getLinkFormData(information.verifiedProductLink),
          productName: information.name,
          webshopLink: getLinkFormData(information.verifiedWebshopLink),
        }))
      : product.productInformations.map<ProductDetails>((information) => ({
          language: information.language,
          productDescription: information.description,
          productLink: getLinkFormData(information.verifiedProductLink),
          productName: information.name,
          webshopLink: getLinkFormData(information.verifiedWebshopLink),
        }));

  const availabilityLanguages: AvailabilityLanguage[] = product.productAvailabilityLanguages.map(
    ({ language }) => ({ value: language })
  );

  const coverImage = Array.isArray(images)
    ? images.find((image) => image.coverPhoto) ?? images[0]
    : undefined;

  const stfCertified = !!product.productCertificates.find((c) => {
    return c.certificate === Certificate.STF;
  });
  const welcomeCyclistCertified = !!product.productCertificates.find(
    (c) => c.certificate === Certificate.WelcomeCyclist
  );

  const getAccessibility = (accessible: boolean | null) => {
    if (accessible !== null) {
      return accessible ? AccessibilityType.Accessible : AccessibilityType.NotAccessible;
    } else {
      return AccessibilityType.Undefined;
    }
  };

  const accessible = getAccessibility(product.accessible);

  const socialMediaId = product.socialMedia?.id || "";
  const companySocialMediaId = product.company.socialMediaId ?? "";
  // if we have both data and socialMediaIds match
  const companySocialMediaEnabled =
    !!socialMediaId && !!companySocialMediaId && socialMediaId === companySocialMediaId;
  const businessHoursId = product.businessHours.id;
  const companyBusinessHoursId = product.company.businessHoursId ?? "";
  const companyHoursEnabled =
    !!businessHoursId && !!companyBusinessHoursId && businessHoursId === companyBusinessHoursId;

  const formValues: MyProductFormValues = {
    accessible,
    availabilityLanguages,
    availabilitySeason: seasonAvailabilityType,
    businessHoursId,
    capacityMax,
    capacityMin,
    city: productAddress?.city || "",
    companyBusinessHoursId,
    companyHoursEnabled,
    companySocialMediaEnabled,
    companySocialMediaId,
    days: { label: days.toString(), value: days },
    details,
    email: product.contactDetails[0].email,
    eventDateRange: {
      endDate,
      startDate,
    },
    hours: { label: hours.toString(), value: hours },
    images: images.map((v) => ({
      altText: v.altText,
      copyright: v.copyright,
      coverPhoto: v === coverImage,
      filename: v.filename,
      largeUrl: v.largeUrl,
      orientation: v.orientation,
      originalHeight: v.originalHeight,
      originalUrl: v.originalUrl,
      originalWidth: v.originalWidth,
      thumbnailUrl: v.thumbnailUrl,
    })),
    latitude: latitude ? String(latitude) : undefined,
    longitude: longitude ? String(longitude) : undefined,
    minutes: { label: minutes.toString(), value: minutes },
    openingHours: mapOpeningHoursToWeek(product.businessHours.default),
    openingHoursExceptions: mapOpeningHoursExceptionsToFormValues(product.businessHours.exceptions),
    originalDetails: originalDetails,
    originalImages: originalImages,
    phonenumber: product.contactDetails[0].phone,
    postalCode: productAddress?.postalCode || "",
    priceFree: pricing?.pricingType === PricingType.Free,
    priceFrom: pricing?.pricingType === PricingType.Chargeable ? priceFrom : "",
    priceTo: pricing?.pricingType === PricingType.Chargeable ? priceTo : "",
    pricingUnit: pricing?.pricingUnit
      ? { value: pricing.pricingUnit }
      : { value: PricingUnit.Placeholder },
    productCategories,
    productSocialMediaLinks:
      product.socialMedia?.socialMediaLinks?.map(({ linkType, verifiedLink }) => ({
        linkType,
        verifiedLink,
      })) || [],
    productType,
    socialMediaId,
    specificMonths: seasonAvailabilityType === AvailabilitySeason.AllYear ? [] : availableMonths,
    stfCertified,
    streetName: productAddress?.streetName || "",
    targetGroups,
    transportationAvailability,
    videos,
    weeks: { label: weeks.toString(), value: weeks },
    welcomeCyclistCertified,
  };

  return formValues;
};
